import { isBilingualMarketingPath, resolveArabicMarketingPath } from './routes';
import { LEGACY_MARKETING_HOSTS, MARKETING_SITE_URL } from './site';

interface MarketingHostRedirectInput {
  host: string;
  pathname: string;
  search: string;
  method: string;
  /** NEXT_PUBLIC_ROOT_DOMAIN without port, e.g. 'asksa.app'. */
  rootDomain: string;
  /**
   * Whether the root domain's homepage is the marketing homepage, i.e. no
   * NEXT_PUBLIC_DEFAULT_LANDING_PAGE. White-label deployments (deempadel.com)
   * serve their own tenant landing there and must keep it.
   */
  rootDomainServesMarketingHome: boolean;
}

/**
 * Where a request should be sent so the marketing site lives on one domain,
 * or null when it should be served where it is.
 *
 * Every route answers on every host, so without this a tenant subdomain, a
 * tenant custom domain or the tenant root domain (www.asksa.app) serves a
 * full indexable copy of /padel-software, /guides/* and the rest. Their
 * canonicals already point at the marketing domain, but a 308 is the
 * stronger signal and stops the copies being indexed, cited or linked.
 *
 * - Legacy marketing hosts (askai.sa) move every path.
 * - Other production hosts move only marketing-only pages, plus the root
 *   domain's homepage when it is the marketing homepage. Tenant homepages,
 *   white-label root homepages, booking, auth and /pages/* stay.
 * - Local, LAN and *.vercel.app preview hosts are left alone so the site can
 *   be reviewed before release.
 */
export function marketingHostRedirect({
  host,
  pathname,
  search,
  method,
  rootDomain,
  rootDomainServesMarketingHome,
}: MarketingHostRedirectInput): URL | null {
  const target = new URL(MARKETING_SITE_URL);
  const normalizedHost = host.split(':')[0]?.toLowerCase() ?? '';

  if (
    (method !== 'GET' && method !== 'HEAD') ||
    pathname.startsWith('/api/') ||
    normalizedHost === target.host ||
    normalizedHost === `www.${target.host}`
  ) {
    return null;
  }

  const isLegacyHost = (LEGACY_MARKETING_HOSTS as readonly string[]).includes(normalizedHost);

  if (!isLegacyHost) {
    if (!isProductionHost(normalizedHost)) {
      return null;
    }

    const isRootDomainHome =
      rootDomainServesMarketingHome &&
      pathname === '/' &&
      (normalizedHost === rootDomain || normalizedHost === `www.${rootDomain}`);

    if (!isRootDomainHome && !isMarketingOnlyPage(pathname)) {
      return null;
    }
  }

  return new URL(`${pathname}${search}`, target);
}

/** A marketing page other than the homepage, in English or under /ar. */
function isMarketingOnlyPage(pathname: string): boolean {
  if (pathname === '/') {
    return false;
  }

  const arabicTarget = resolveArabicMarketingPath(pathname);

  if (arabicTarget !== null) {
    return arabicTarget !== '/';
  }

  return isBilingualMarketingPath(pathname);
}

export function isProductionHost(host: string): boolean {
  return !(
    host === '' ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === 'lvh.me' ||
    host.endsWith('.localhost') ||
    host.endsWith('.lvh.me') ||
    host.endsWith('.vercel.app') ||
    host.endsWith('.railway.app') ||
    /^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.)/.test(host)
  );
}
